import {Component, EventEmitter, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ClientService} from '../../../clients-module/services/client.service';
import {ToastrService} from 'ngx-toastr';
import {AuthService} from '../../../services/auth.service';
import {BankruptcySettingModel} from '../../Models/criminal-model';
import {DecisionGridBankruptcyService} from '../../Services/decision-grid-bankruptcy.service';
import { Subscription } from 'rxjs';
import { GroupService } from '../../Services/groups.service';

@Component({
  selector: 'app-criminal-grid-bankruptcy',
  templateUrl: './criminal-grid-bankruptcy.component.html',
  styleUrls: ['./criminal-grid-bankruptcy.component.less'],
  encapsulation: ViewEncapsulation.None,
})
export class CriminalGridBankruptcyComponent implements OnInit, OnDestroy {

  public form: UntypedFormGroup;
  @Input() reset: EventEmitter<any>;
  private reset$: Subscription;

  constructor(
    private readonly decisionGridBankruptcyService: DecisionGridBankruptcyService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly clientService: ClientService,
    private readonly toast: ToastrService,
    private readonly auth: AuthService,
    public readonly groupService: GroupService,
  ) {
  }
  ngOnDestroy(): void {
    if(this.reset$) this.reset$.unsubscribe();
  }

  public ngOnInit(): void {

    this.createForm();

  }

  public createForm() {

    this.form = this.formBuilder.group({
      bankruptcy: this.formBuilder.array([])
    });

    this.decisionGridBankruptcyService.bankruptcySettingsSubscription$.subscribe((r: BankruptcySettingModel[]) => {

      const f = <UntypedFormArray>this.form.controls.bankruptcy;
      // Clear all existing
      f.clear();

      r.forEach((q: BankruptcySettingModel) => {

        q.isOverride = q.groupId > 0;

        f.push(new UntypedFormGroup({
          howManyAllowed: new UntypedFormControl(q.howManyAllowed, [Validators.required]),
          howLong: new UntypedFormControl(q.howLong, [Validators.required]),
          decision: new UntypedFormControl(q.decision, [Validators.required]),
          chargeType: new UntypedFormControl(q.chargeType, [Validators.required]),
          settingId: new UntypedFormControl(q.settingId, [Validators.required]),
          considerDischarged: new UntypedFormControl(q.considerDischarged, [Validators.required]),
          clientId: new UntypedFormControl(q.clientId, [Validators.required]),
          groupId: new UntypedFormControl(q.groupId),
          isOverride: new UntypedFormControl(q.isOverride)
        }));
      });
    });
  }

  public track(item: any, index: number) {
    return index;
  }

  public newSetting(): void {
    const f = <UntypedFormArray>this.form.controls.bankruptcy;

    // First, check the last one in. If that setting is invalid, do not allow them to add another one.

    f.push(new UntypedFormGroup({
      howManyAllowed: new UntypedFormControl(null, [Validators.required]),
      howLong: new UntypedFormControl(null, [Validators.required]),
      decision: new UntypedFormControl(null, [Validators.required]),
      chargeType: new UntypedFormControl(0, [Validators.required]),
      settingId: new UntypedFormControl(0, [Validators.required]),
      considerDischarged: new UntypedFormControl(false, [Validators.required]),
      clientId: new UntypedFormControl(this.clientService.currentClient.id, [Validators.required]),
    }));
  }

  public saveBankruptcySetting(index: number): void {
    const f = <UntypedFormArray>this.form.controls.bankruptcy;

    if (!f.controls[index].valid) {
      this.toast.warning('Bankruptcy setting has errors. Please correct', 'Setting Invalid');
    }

    const settingId = f.controls[index].get('settingId').value;
    const clientId = this.clientService.currentClient.id;

    const formValue = f.controls[index].value;

    if (settingId > 0) {
      // This is an edit
      this.decisionGridBankruptcyService.updateBankruptcySetting(clientId, formValue).subscribe((r: BankruptcySettingModel) => {
        this.toast.success('Bankruptcy setting successful modified', 'Save Successful');
        f.controls[index].markAsPristine();
        f.controls[index].markAsUntouched();
      });

    } else {
      // This is a new one
      this.decisionGridBankruptcyService.insertBankruptcySetting(clientId, formValue).subscribe((r: BankruptcySettingModel) => {
        this.toast.success('Bankruptcy setting successful added', 'Save Successful');

        f.controls[index].get('settingId').setValue(r.settingId);

        f.controls[index].markAsPristine();
        f.controls[index].markAsUntouched();
      });
    }
  }

  public deleteBankruptcySetting(index: number): void {

    const f = <UntypedFormArray>this.form.controls.bankruptcy;
    const settingId = f.controls[index].get('settingId').value;

    this.decisionGridBankruptcyService.deleteBankruptcySetting(this.clientService.currentClient.id, settingId).subscribe((r: any) => {
      // remove this from the array
      f.removeAt(index);
      this.toast.success('Bankruptcy setting Successful removed', 'Save Successful');
    });
  }

  public cancelBankruptcySetting(index: number): void {

    const f = <UntypedFormArray>this.form.controls.bankruptcy;

    // First get the original settings
    const originalSetting = this.decisionGridBankruptcyService.bankruptcySettings.find(w => w.settingId === f.controls[index].get('settingId').value);

    f.controls[index].reset(originalSetting);
  }

  public isFormDirty(): boolean {
    return this.form.controls?.howManyAllowed?.dirty ||
      this.form.controls?.howLong?.dirty ||
      this.form.controls?.decision?.dirty;
  }

  public propertyOverride(e: BankruptcySettingModel): void {
    console.log(e);
    let item = new BankruptcySettingModel();
    const f = <UntypedFormArray>this.form.controls.bankruptcy;

    if (e) {
      item = e;
    }

    const rowData = this.formBuilder.group({
      id: 0,
      clientId: [this.clientService.currentClient.id, Validators.required],
      userId: [this.auth.loggedInUser.id, Validators.required],
      groupId: [item.groupId, Validators.required],
      howManyAllowed: [0, Validators.required],
      howLong: [0, Validators.required],
      decision: [item.decision, Validators.required],
      isOverride: [true],
      settingId: [0]
    });

    f.push(rowData);
  }
}
